import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import { encodePathSegment } from '../../lib/url';

interface RateLimit {
  perMinute?: number;
  perDay?: number;
}

interface Policy {
  id: string;
  ownerDid: string;
  name: string;
  version: number;
  allowedScopes: string[];
  defaultTtlSeconds: number;
  maxTtlSeconds: number;
  redeemerAllowlist: string[] | null;
  rateLimits?: Record<string, RateLimit>;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  revokedAt?: string | null;
}

interface ListEnvelope {
  items?: Policy[];
  data?: Policy[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

interface RateLimitRow {
  id: string;
  scope: string;
  perMinute: string;
  perDay: string;
}

interface PolicyFormState {
  id?: string;
  ifVersion?: number;
  name: string;
  scopes: string[];
  scopeInput: string;
  defaultTtlSeconds: string;
  maxTtlSeconds: string;
  anyAuthenticated: boolean;
  allowlist: string[];
  allowlistInput: string;
  rateLimits: RateLimitRow[];
}

const NAME_REGEX = /^[a-z][a-z0-9_-]{2,63}$/;

const emptyForm: PolicyFormState = {
  name: '',
  scopes: [],
  scopeInput: '',
  defaultTtlSeconds: '300',
  maxTtlSeconds: '3600',
  anyAuthenticated: true,
  allowlist: [],
  allowlistInput: '',
  rateLimits: [],
};

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `r_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function formatTtl(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function summariseAllowlist(policy: Policy, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (policy.redeemerAllowlist === null) return t('policies.anyAuthenticated');
  if (policy.redeemerAllowlist.length === 0) return t('policies.killSwitch');
  return t('policies.explicit', { count: policy.redeemerAllowlist.length });
}

function policyToForm(policy: Policy): PolicyFormState {
  return {
    id: policy.id,
    ifVersion: policy.version,
    name: policy.name,
    scopes: [...policy.allowedScopes],
    scopeInput: '',
    defaultTtlSeconds: String(policy.defaultTtlSeconds),
    maxTtlSeconds: String(policy.maxTtlSeconds),
    anyAuthenticated: policy.redeemerAllowlist === null,
    allowlist: policy.redeemerAllowlist ? [...policy.redeemerAllowlist] : [],
    allowlistInput: '',
    rateLimits: Object.entries(policy.rateLimits ?? {}).map(([scope, lim]) => ({
      id: uuid(),
      scope,
      perMinute: lim?.perMinute !== undefined ? String(lim.perMinute) : '',
      perDay: lim?.perDay !== undefined ? String(lim.perDay) : '',
    })),
  };
}

function diffArrays(prev: string[], next: string[]): { added: string[]; removed: string[] } {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  return {
    added: next.filter((x) => !prevSet.has(x)),
    removed: prev.filter((x) => !nextSet.has(x)),
  };
}

export default function AgentPoliciesPage() {
  const { did } = useParams<{ did: string }>();
  const { t } = useTranslation(['agents', 'common']);

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const [modalKind, setModalKind] = useState<'edit' | 'delete' | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [form, setForm] = useState<PolicyFormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = did ? `?ownerDid=${encodeURIComponent(did)}` : '';
      const res = await apiFetch<ListEnvelope>(`/operators/me/policies${params}`);
      const items = Array.isArray(res.items) ? res.items : Array.isArray(res.data) ? res.data : [];
      setPolicies(items);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common:errors.unknown'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!toast) return;
    const handle = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(handle);
  }, [toast]);

  const openNew = () => {
    setEditingPolicy(null);
    setForm({ ...emptyForm });
    setFormError(null);
    setModalKind('edit');
  };

  const openEdit = (policy: Policy) => {
    setEditingPolicy(policy);
    setForm(policyToForm(policy));
    setFormError(null);
    setModalKind('edit');
  };

  const openDelete = (policy: Policy) => {
    setEditingPolicy(policy);
    setFormError(null);
    setModalKind('delete');
  };

  const closeModal = () => {
    setModalKind(null);
    setEditingPolicy(null);
    setForm(emptyForm);
    setFormError(null);
  };

  // ---- Tag-input helpers (scopes + allowlist) ----
  const commitScope = () => {
    const v = form.scopeInput.trim();
    if (!v) return;
    if (form.scopes.includes(v)) {
      setForm((f) => ({ ...f, scopeInput: '' }));
      return;
    }
    setForm((f) => ({ ...f, scopes: [...f.scopes, v], scopeInput: '' }));
  };

  const removeScope = (scope: string) => {
    setForm((f) => ({ ...f, scopes: f.scopes.filter((s) => s !== scope) }));
  };

  const commitAllowlist = () => {
    const v = form.allowlistInput.trim();
    if (!v) return;
    if (form.allowlist.includes(v)) {
      setForm((f) => ({ ...f, allowlistInput: '' }));
      return;
    }
    setForm((f) => ({ ...f, allowlist: [...f.allowlist, v], allowlistInput: '' }));
  };

  const removeAllowlistEntry = (entry: string) => {
    setForm((f) => ({ ...f, allowlist: f.allowlist.filter((d) => d !== entry) }));
  };

  // ---- Rate limit row helpers ----
  const addRateLimitRow = () => {
    setForm((f) => ({
      ...f,
      rateLimits: [...f.rateLimits, { id: uuid(), scope: '', perMinute: '', perDay: '' }],
    }));
  };

  const updateRateLimit = (id: string, patch: Partial<RateLimitRow>) => {
    setForm((f) => ({
      ...f,
      rateLimits: f.rateLimits.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  };

  const removeRateLimit = (id: string) => {
    setForm((f) => ({ ...f, rateLimits: f.rateLimits.filter((r) => r.id !== id) }));
  };

  // ---- Diff preview ----
  const diff = useMemo(() => {
    if (!editingPolicy) return null;
    const scopeDiff = diffArrays(editingPolicy.allowedScopes, form.scopes);
    const prevAllowlist = editingPolicy.redeemerAllowlist ?? [];
    const nextAllowlist = form.anyAuthenticated ? [] : form.allowlist;
    const allowlistDiff =
      editingPolicy.redeemerAllowlist === null && form.anyAuthenticated
        ? { added: [], removed: [] }
        : diffArrays(prevAllowlist, nextAllowlist);
    const ttlChanged =
      Number(form.defaultTtlSeconds) !== editingPolicy.defaultTtlSeconds ||
      Number(form.maxTtlSeconds) !== editingPolicy.maxTtlSeconds;
    const allowlistKindChanged =
      (editingPolicy.redeemerAllowlist === null) !== form.anyAuthenticated;
    const noChanges =
      scopeDiff.added.length === 0 &&
      scopeDiff.removed.length === 0 &&
      allowlistDiff.added.length === 0 &&
      allowlistDiff.removed.length === 0 &&
      !ttlChanged &&
      !allowlistKindChanged;
    return {
      scopes: scopeDiff,
      allowlist: allowlistDiff,
      allowlistKindChanged,
      defaultTtl: {
        from: editingPolicy.defaultTtlSeconds,
        to: Number(form.defaultTtlSeconds),
        changed: Number(form.defaultTtlSeconds) !== editingPolicy.defaultTtlSeconds,
      },
      maxTtl: {
        from: editingPolicy.maxTtlSeconds,
        to: Number(form.maxTtlSeconds),
        changed: Number(form.maxTtlSeconds) !== editingPolicy.maxTtlSeconds,
      },
      noChanges,
    };
  }, [editingPolicy, form]);

  const validate = (): string | null => {
    if (!editingPolicy && !NAME_REGEX.test(form.name.trim())) {
      return t('policies.errors.nameInvalid');
    }
    if (form.scopes.length === 0) return t('policies.errors.scopesRequired');
    const def = Number(form.defaultTtlSeconds);
    const mx = Number(form.maxTtlSeconds);
    if (!Number.isFinite(mx) || mx <= 0 || mx > 86400) return t('policies.errors.maxTtlRange');
    if (!Number.isFinite(def) || def < 60 || def > mx) return t('policies.errors.ttlRange');
    return null;
  };

  const buildBody = (forCreate = false) => {
    const rateLimits: Record<string, RateLimit> = {};
    for (const row of form.rateLimits) {
      const scope = row.scope.trim();
      if (!scope) continue;
      const entry: RateLimit = {};
      if (row.perMinute.trim()) entry.perMinute = Number(row.perMinute);
      if (row.perDay.trim()) entry.perDay = Number(row.perDay);
      if (Object.keys(entry).length > 0) rateLimits[scope] = entry;
    }
    return {
      ...(forCreate && did ? { ownerDid: did } : {}),
      name: form.name.trim(),
      allowedScopes: form.scopes,
      defaultTtlSeconds: Number(form.defaultTtlSeconds),
      maxTtlSeconds: Number(form.maxTtlSeconds),
      redeemerAllowlist: form.anyAuthenticated ? null : form.allowlist,
      rateLimits,
    };
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const isUpdate = Boolean(form.id);
      const body = buildBody(!isUpdate) as Record<string, unknown>;
      if (isUpdate) {
        body.ifVersion = form.ifVersion ?? 1;
        // PUT — name is treated server-side as immutable on update; safe to omit.
        delete body.name;
        await apiFetch(`/operators/me/policies/${encodePathSegment(form.id ?? '')}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        setToast({ kind: 'success', msg: t('policies.toasts.updateSuccess') });
      } else {
        await apiFetch(`/operators/me/policies`, {
          method: 'POST',
          headers: { 'Idempotency-Key': uuid() },
          body: JSON.stringify(body),
        });
        setToast({ kind: 'success', msg: t('policies.toasts.createSuccess') });
      }
      closeModal();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('policies.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingPolicy) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await apiFetch(`/operators/me/policies/${encodePathSegment(editingPolicy.id)}`, {
        method: 'DELETE',
      });
      setToast({ kind: 'success', msg: t('policies.toasts.deleteSuccess') });
      closeModal();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('policies.errors.delete'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!did) {
    return <div className="text-center py-20 text-red-500">{t('common:errors.unknown')}</div>;
  }

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-xs text-text-faint">
        <Link to="/ai-agents" className="hover:text-primary">{t('detail.breadcrumbBase')}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <Link to={`/ai-agents/${did}`} className="hover:text-primary">{did}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <span className="font-semibold text-text-main">{t('policies.title')}</span>
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-text-main">{t('policies.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('policies.subtitle')}</p>
        </div>
        <button onClick={openNew} className="btn-primary text-xs">
          <span className="material-symbols-outlined mr-1" style={{ fontSize: 14 }}>add</span>
          {t('policies.newPolicy')}
        </button>
      </div>

      {toast && (
        <div
          className={`rounded-lg border px-4 py-2 text-xs font-semibold ${
            toast.kind === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="rounded-xl border border-border-subtle bg-white shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle bg-slate-50/50">
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
                {t('policies.table.name')}
              </th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
                {t('policies.table.scopes')}
              </th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
                {t('policies.table.defaultTtl')}
              </th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
                {t('policies.table.allowlist')}
              </th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
                {t('policies.table.updated')}
              </th>
              <th className="w-32 px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-text-faint">
                  {t('common:loading')}
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-red-500">{error}</td>
              </tr>
            ) : policies.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-text-faint">
                  {t('policies.empty')}
                </td>
              </tr>
            ) : (
              policies.map((policy) => (
                <tr key={policy.id} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <div className="flex flex-col">
                      <span className="font-mono text-xs font-bold text-text-main">{policy.name}</span>
                      <span className="text-[10px] text-text-faint">v{policy.version}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {policy.allowedScopes.slice(0, 4).map((s) => (
                        <span
                          key={s}
                          className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700"
                        >
                          {s}
                        </span>
                      ))}
                      {policy.allowedScopes.length > 4 && (
                        <span className="text-[10px] text-text-faint">
                          +{policy.allowedScopes.length - 4}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-xs text-text-main">
                      {policy.defaultTtlSeconds}s
                    </span>
                    <p className="text-[10px] text-text-faint">{formatTtl(policy.defaultTtlSeconds)}</p>
                  </td>
                  <td className="px-5 py-3">
                    {policy.redeemerAllowlist === null ? (
                      <Badge variant="info">{t('policies.anyAuthenticated')}</Badge>
                    ) : policy.redeemerAllowlist.length === 0 ? (
                      <Badge variant="error">{t('policies.killSwitch')}</Badge>
                    ) : (
                      <Badge variant="neutral">{summariseAllowlist(policy, t)}</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs text-text-muted">
                      {new Date(policy.updatedAt).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(policy)}
                        className="rounded-lg p-1 text-text-faint hover:bg-slate-100 hover:text-text-main"
                        title={t('policies.edit')}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                      </button>
                      <button
                        onClick={() => openDelete(policy)}
                        className="rounded-lg p-1 text-text-faint hover:bg-red-50 hover:text-red-600"
                        title={t('policies.delete')}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New / Edit modal */}
      <Modal
        open={modalKind === 'edit'}
        onClose={closeModal}
        wide
        title={editingPolicy ? t('policies.modal.editTitle') : t('policies.modal.newTitle')}
      >
        <div className="space-y-4">
          {!editingPolicy && (
            <FormField
              label={t('policies.fields.name')}
              hint={t('policies.fields.nameHint')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="default-tool-policy"
            />
          )}

          {/* Scopes tag input */}
          <div>
            <label className="mb-1.5 block text-xs font-bold text-text-muted">
              {t('policies.fields.scopes')}
            </label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border-stroke bg-white px-2 py-1.5">
              {form.scopes.map((scope) => (
                <span
                  key={scope}
                  className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700"
                >
                  {scope}
                  <button
                    type="button"
                    onClick={() => removeScope(scope)}
                    className="rounded-full text-blue-700/70 hover:text-blue-900"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>close</span>
                  </button>
                </span>
              ))}
              <input
                value={form.scopeInput}
                onChange={(e) => setForm({ ...form, scopeInput: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    commitScope();
                  } else if (e.key === 'Backspace' && !form.scopeInput && form.scopes.length > 0) {
                    removeScope(form.scopes[form.scopes.length - 1]);
                  }
                }}
                onBlur={commitScope}
                placeholder={t('policies.fields.scopesPlaceholder')}
                className="flex-1 min-w-[10rem] bg-transparent text-xs text-text-main focus:outline-none"
              />
            </div>
            <p className="mt-1 text-[10px] text-text-faint">{t('policies.fields.scopesHint')}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <FormField
                label={t('policies.fields.defaultTtl')}
                type="number"
                value={form.defaultTtlSeconds}
                onChange={(e) => setForm({ ...form, defaultTtlSeconds: e.target.value })}
              />
              <p className="mt-1 text-[10px] text-text-faint">
                {formatTtl(Number(form.defaultTtlSeconds) || 0)}
              </p>
            </div>
            <div>
              <FormField
                label={t('policies.fields.maxTtl')}
                type="number"
                value={form.maxTtlSeconds}
                onChange={(e) => setForm({ ...form, maxTtlSeconds: e.target.value })}
              />
              <p className="mt-1 text-[10px] text-text-faint">
                {formatTtl(Number(form.maxTtlSeconds) || 0)}
              </p>
            </div>
          </div>

          {/* Redeemer allowlist */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-xs font-bold text-text-muted">
                {t('policies.fields.redeemerAllowlist')}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-[11px] font-semibold text-text-muted">
                <input
                  type="checkbox"
                  checked={form.anyAuthenticated}
                  onChange={(e) => setForm({ ...form, anyAuthenticated: e.target.checked })}
                  className="rounded"
                />
                {t('policies.fields.anyAuthenticatedToggle')}
              </label>
            </div>
            <div
              className={`flex flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 ${
                form.anyAuthenticated ? 'border-slate-200 opacity-50' : 'border-border-stroke'
              }`}
            >
              {form.allowlist.map((entry) => (
                <span
                  key={entry}
                  className="inline-flex items-center gap-1 rounded bg-purple-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-purple-700"
                >
                  {entry}
                  <button
                    type="button"
                    onClick={() => removeAllowlistEntry(entry)}
                    className="rounded-full text-purple-700/70 hover:text-purple-900"
                    disabled={form.anyAuthenticated}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>close</span>
                  </button>
                </span>
              ))}
              <input
                value={form.allowlistInput}
                onChange={(e) => setForm({ ...form, allowlistInput: e.target.value })}
                disabled={form.anyAuthenticated}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    commitAllowlist();
                  } else if (
                    e.key === 'Backspace' &&
                    !form.allowlistInput &&
                    form.allowlist.length > 0
                  ) {
                    removeAllowlistEntry(form.allowlist[form.allowlist.length - 1]);
                  }
                }}
                onBlur={commitAllowlist}
                placeholder={t('policies.fields.redeemerPlaceholder')}
                className="flex-1 min-w-[10rem] bg-transparent text-xs text-text-main focus:outline-none disabled:cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-[10px] text-text-faint">{t('policies.fields.redeemerHint')}</p>
          </div>

          {/* Rate-limit row builder */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-xs font-bold text-text-muted">
                {t('policies.fields.rateLimits')}
              </label>
              <button
                type="button"
                onClick={addRateLimitRow}
                className="text-[10px] font-bold text-primary hover:underline"
              >
                {t('policies.fields.addRateLimit')}
              </button>
            </div>
            <div className="space-y-2">
              {form.rateLimits.map((row) => (
                <div key={row.id} className="grid grid-cols-12 items-end gap-2">
                  <div className="col-span-5">
                    <FormField
                      label={t('policies.fields.rateScope')}
                      value={row.scope}
                      onChange={(e) => updateRateLimit(row.id, { scope: e.target.value })}
                      placeholder="tool:weather.lookup"
                    />
                  </div>
                  <div className="col-span-3">
                    <FormField
                      label={t('policies.fields.perMinute')}
                      type="number"
                      value={row.perMinute}
                      onChange={(e) => updateRateLimit(row.id, { perMinute: e.target.value })}
                    />
                  </div>
                  <div className="col-span-3">
                    <FormField
                      label={t('policies.fields.perDay')}
                      type="number"
                      value={row.perDay}
                      onChange={(e) => updateRateLimit(row.id, { perDay: e.target.value })}
                    />
                  </div>
                  <div className="col-span-1 pb-1">
                    <button
                      type="button"
                      onClick={() => removeRateLimit(row.id)}
                      className="rounded-lg p-1 text-text-faint hover:bg-red-50 hover:text-red-600"
                      title={t('policies.delete')}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Diff preview */}
          {editingPolicy && diff && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700">
                {t('policies.diff.title')}
              </p>
              {diff.noChanges ? (
                <p className="mt-2 text-xs text-amber-700">{t('policies.diff.noChanges')}</p>
              ) : (
                <ul className="mt-2 space-y-1 text-xs text-amber-900">
                  {(diff.scopes.added.length > 0 || diff.scopes.removed.length > 0) && (
                    <li>
                      <span className="font-bold">{t('policies.diff.scopes')}:</span>{' '}
                      {diff.scopes.added.length > 0 && (
                        <span className="text-emerald-700">
                          + {diff.scopes.added.join(', ')}
                        </span>
                      )}
                      {diff.scopes.added.length > 0 && diff.scopes.removed.length > 0 && '  '}
                      {diff.scopes.removed.length > 0 && (
                        <span className="text-red-700">
                          − {diff.scopes.removed.join(', ')}
                        </span>
                      )}
                    </li>
                  )}
                  {diff.defaultTtl.changed && (
                    <li>
                      <span className="font-bold">{t('policies.diff.defaultTtl')}:</span>{' '}
                      {diff.defaultTtl.from}s → {diff.defaultTtl.to}s
                    </li>
                  )}
                  {diff.maxTtl.changed && (
                    <li>
                      <span className="font-bold">{t('policies.diff.maxTtl')}:</span>{' '}
                      {diff.maxTtl.from}s → {diff.maxTtl.to}s
                    </li>
                  )}
                  {(diff.allowlistKindChanged ||
                    diff.allowlist.added.length > 0 ||
                    diff.allowlist.removed.length > 0) && (
                    <li>
                      <span className="font-bold">{t('policies.diff.allowlist')}:</span>{' '}
                      {diff.allowlistKindChanged && (
                        <span>
                          {editingPolicy.redeemerAllowlist === null
                            ? t('policies.anyAuthenticated')
                            : t('policies.explicit', { count: editingPolicy.redeemerAllowlist?.length ?? 0 })}
                          {' → '}
                          {form.anyAuthenticated
                            ? t('policies.anyAuthenticated')
                            : form.allowlist.length === 0
                              ? t('policies.killSwitch')
                              : t('policies.explicit', { count: form.allowlist.length })}
                        </span>
                      )}
                      {!diff.allowlistKindChanged && diff.allowlist.added.length > 0 && (
                        <span className="text-emerald-700">
                          {' '}+ {diff.allowlist.added.join(', ')}
                        </span>
                      )}
                      {!diff.allowlistKindChanged && diff.allowlist.removed.length > 0 && (
                        <span className="text-red-700">
                          {' '}− {diff.allowlist.removed.join(', ')}
                        </span>
                      )}
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}

          <div className="flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
            <button
              onClick={closeModal}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary text-xs disabled:opacity-50"
            >
              {submitting
                ? t('common:saving')
                : editingPolicy
                  ? t('policies.modal.save')
                  : t('policies.modal.create')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        open={modalKind === 'delete'}
        onClose={closeModal}
        title={t('policies.modal.deleteTitle')}
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            {t('policies.modal.deleteBody', { name: editingPolicy?.name ?? '' })}
          </p>
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
            <button
              onClick={closeModal}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleDelete}
              disabled={submitting}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? t('common:saving') : t('policies.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
